// Read-only preview rendering for the page editor's section canvas. Mirrors the
// published site's section markup (same class scheme as the template's
// render-blocks.ts sectionShell) inside a `.pgs-site` scope styled by
// section-canvas.css with the site's design tokens.
//
// Inline formatting (b/i/a/code/mark from the Editor.js toolbar) is kept via
// the same tag allowlist as the template's sanitizer; everything else is
// escaped — preview HTML is injected via dangerouslySetInnerHTML, so this
// module is the XSS boundary for the canvas. Anchors render without href (a
// click on the preview must edit, not navigate).

import type { CSSProperties } from "react";
import { DEFAULT_SECTION_PADDING, SAFE_COLOR_RE, safeColor, sectionTextColor } from "./editor-content";
import type { PageSection, SectionBackground, SectionColumn, SectionTimeline } from "./editor-content";

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);
}

// --- Inline-mark sanitizer -------------------------------------------------
// Mirror of the template's sanitizeInline (apps/template/src/lib/
// render-blocks.ts) minus link hrefs — the template can't depend on workspace
// packages (it deploys standalone per tenant), so the two copies are kept in
// step by hand, like SAFE_COLOR_RE below.

const ALLOWED_TAGS = new Set(["a", "b", "strong", "i", "em", "code", "mark", "br", "span"]);
// Mirror of the template's span handling: one fs-* type-scale class and a
// colour-shaped `color` style survive; all other span attributes are dropped.
const FONT_SIZE_CLASS_RE = /^fs-(?:xs|base|h[1-6]|d[1-4])$/;

/** Escape for HTML body context, preserving existing entity references. */
function escapeText(value: unknown): string {
  return String(value ?? "")
    .replace(/&(?!#?[a-zA-Z0-9]+;)/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Sanitize a single tag token: allowed inline marks are re-permitted with all
 * attributes stripped (anchors lose their href — the canvas is click-to-edit);
 * anything else is dropped.
 */
function sanitizeTag(raw: string): string {
  const inner = raw.slice(1, -1).trim();
  const isClosing = inner.startsWith("/");
  const afterSlash = isClosing ? inner.slice(1).trim() : inner;
  const body = afterSlash.endsWith("/") ? afterSlash.slice(0, -1).trim() : afterSlash;

  const nameMatch = body.match(/^([a-zA-Z][a-zA-Z0-9]*)/);
  if (!nameMatch) return "";
  const name = nameMatch[1].toLowerCase();
  if (!ALLOWED_TAGS.has(name)) return "";
  if (name === "br") return "<br>";
  if (isClosing) return `</${name}>`;

  if (name === "span") {
    const sizeClass = (attrValue(body, "class") ?? "")
      .split(/\s+/)
      .find((cls) => FONT_SIZE_CLASS_RE.test(cls));
    const colorMatch = (attrValue(body, "style") ?? "").match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
    const color = colorMatch && SAFE_COLOR_RE.test(colorMatch[1].trim()) ? colorMatch[1].trim() : "";
    let attrs = "";
    if (sizeClass) attrs += ` class="${sizeClass}"`;
    if (color) attrs += ` style="color:${color}"`;
    return `<span${attrs}>`;
  }

  return `<${name}>`;
}

/** Extract one attribute's raw value from a tag body. */
function attrValue(body: string, attr: string): string | null {
  const match = body.match(new RegExp(`\\b${attr}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, "i"));
  return match ? (match[2] ?? match[3] ?? match[4] ?? "") : null;
}

/** Editor.js inline HTML → the allowlisted marks; all text escaped. */
function inline(input: unknown): string {
  if (input == null) return "";
  const str = String(input);
  let out = "";
  let i = 0;

  while (i < str.length) {
    const lt = str.indexOf("<", i);
    if (lt === -1) {
      out += escapeText(str.slice(i));
      break;
    }
    out += escapeText(str.slice(i, lt));

    // Only treat "<" as a tag start when a tag name or "/" follows; otherwise
    // it's a stray less-than (e.g. "5 < 10") and is escaped as text.
    const next = str[lt + 1];
    if (!next || !/[a-zA-Z/]/.test(next)) {
      out += "&lt;";
      i = lt + 1;
      continue;
    }

    const gt = str.indexOf(">", lt);
    if (gt === -1) {
      // Unterminated "<": treat the remainder as text.
      out += escapeText(str.slice(lt));
      break;
    }

    out += sanitizeTag(str.slice(lt, gt + 1));
    i = gt + 1;
  }

  return out;
}

// SAFE_COLOR_RE / safeColor are shared from editor-content.ts (one source of
// truth); mirrored by the template's renderer (apps/template/src/lib/
// render-blocks.ts) since it can't depend on workspace packages.

/** The colour layer as a CSS gradient (a solid becomes a two-stop gradient
 *  so it can stack over the image); "" when none/invalid. */
function colorLayerGradient(layer: SectionBackground["color"]): string {
  if (layer.type === "color") {
    const color = safeColor(layer.color);
    return color ? `linear-gradient(${color}, ${color})` : "";
  }
  if (layer.type === "gradient") {
    const from = safeColor(layer.from);
    const to = safeColor(layer.to);
    return from && to ? `linear-gradient(135deg, ${from}, ${to})` : "";
  }
  return "";
}

export function backgroundPreviewStyle(background: SectionBackground): CSSProperties {
  const imageUrl = background.imageKey && background.imageUrl
    ? `url("${background.imageUrl.replace(/["\\]/g, "")}")`
    : "";

  if (imageUrl) {
    // The colour layer (if any) tints the image by stacking above it.
    const tint = colorLayerGradient(background.color);
    return { backgroundImage: tint ? `${tint}, ${imageUrl}` : imageUrl };
  }
  if (background.color.type === "color") {
    const color = safeColor(background.color.color);
    return color ? { backgroundColor: color } : {};
  }
  const gradient = colorLayerGradient(background.color);
  return gradient ? { backgroundImage: gradient } : {};
}

export interface SectionPreviewShell {
  /** Classes for the <section> element (site scheme, under .pgs-site). */
  className: string;
  /** Inline background style. */
  style: CSSProperties;
  /** Classes for the inner width wrapper. */
  innerClassName: string;
  /** True when the section has an image background but only a stored key (no
   *  preview URL) — the canvas shows a dark stand-in so overlay text reads. */
  imagePending: boolean;
}

export function sectionPreviewShell(section: PageSection): SectionPreviewShell {
  const { background, width, align, columns, fullHeight, padding } = section.settings;

  let className = "content-section page-section";
  if (align !== "left") className += ` page-section--align-${align}`;
  if (fullHeight) className += " page-section--full";
  if (padding !== DEFAULT_SECTION_PADDING) className += ` page-section--pad-${padding}`;
  const hasImage = Boolean(background.imageKey);
  if (hasImage) {
    className += " page-section--bg-image";
    // A colour layer replaces the default dark scrim as the image tint.
    if (background.color.type !== "none") className += " page-section--bg-tinted";
  }

  let innerClassName = "container";
  if (width !== "normal") innerClassName += ` container--${width}`;
  if (columns > 1) innerClassName += ` page-section__cols page-section__cols--${columns}`;

  // Text colour is derived from the colour layer we set, ignoring the image —
  // recomputed live so the canvas reacts as the tint changes. Applied as the
  // site text tokens plus `color` (matches the column editor surface).
  const textColor = sectionTextColor(background);
  const style: CSSProperties = {
    ...backgroundPreviewStyle(background),
    ...(textColor
      ? ({ "--text": textColor, "--text-muted": textColor, color: textColor } as CSSProperties)
      : {}),
  };

  return {
    className,
    style,
    innerClassName,
    imagePending: hasImage && !background.imageUrl,
  };
}

/** Clamp a bar-list fill percentage to an integer 0–100 (default: full). */
function barPercent(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 100;
  return Math.min(Math.max(Math.round(n), 0), 100);
}

/**
 * The image block's display width as a CSS declaration ("" = natural/full):
 * { size: { value, unit: "%" | "px" | "rem" } }, or the older bare
 * widthPercent. Clamped numerics with an allowlisted unit only — mirrors the
 * template renderer's imageWidthStyle.
 */
function imageWidthStyle(d: Record<string, any>): string {
  const size = d.size;
  if (size && typeof size === "object") {
    const n = Number(size.value);
    if (!Number.isFinite(n)) return "";
    if (size.unit === "px") return `width:${Math.min(Math.max(Math.round(n), 20), 2000)}px`;
    if (size.unit === "rem") return `width:${Math.min(Math.max(Math.round(n * 4) / 4, 1), 120)}rem`;
    if (size.unit === "%") {
      const pct = Math.min(Math.max(Math.round(n), 10), 100);
      return pct < 100 ? `width:${pct}%` : "";
    }
    return "";
  }
  const legacy = Number(d.widthPercent);
  if (Number.isFinite(legacy)) {
    const pct = Math.min(Math.max(Math.round(legacy), 10), 100);
    return pct < 100 ? `width:${pct}%` : "";
  }
  return "";
}

function previewBlock(block: Record<string, any>): string {
  const d = block?.data ?? {};
  switch (block?.type) {
    case "header": {
      const level = Math.min(Math.max(Number(d.level) || 2, 1), 6);
      return `<h${level}>${inline(d.text)}</h${level}>`;
    }
    case "paragraph":
      return `<p>${inline(d.text)}</p>`;
    case "list": {
      const tag = d.style === "ordered" ? "ol" : "ul";
      const items = (Array.isArray(d.items) ? d.items : [])
        .map((it: any) => `<li>${inline(typeof it === "string" ? it : it?.content)}</li>`)
        .join("");
      return `<${tag}>${items}</${tag}>`;
    }
    case "quote":
      return `<blockquote><p>${inline(d.text)}</p></blockquote>`;
    case "image": {
      if (!d.file?.url) return "";
      // Editor-chosen display width (% / px / rem; clamped — mirrors the
      // template renderer).
      const widthDecl = imageWidthStyle(d);
      const style = widthDecl ? ` style="${widthDecl}"` : "";
      return `<img src="${esc(d.file.url)}" alt="${esc(d.caption ?? "")}"${style} />`;
    }
    case "delimiter":
      return "<hr />";
    case "cta": {
      // One or more buttons ({ buttons: [...] }; legacy single-button fields
      // at the top level). Custom colours: bg picked, fg a11y-derived on save.
      const defs: Array<Record<string, any>> = Array.isArray(d.buttons) ? d.buttons : [d];
      const buttons = defs
        .filter((btn) => btn?.label)
        .map((btn) => {
          const bg = safeColor(typeof btn.bg === "string" ? btn.bg : undefined);
          const fg = safeColor(typeof btn.fg === "string" ? btn.fg : undefined);
          const style = bg ? ` style="background:${esc(bg)}${fg ? `;color:${esc(fg)}` : ""}"` : "";
          return `<span class="btn btn-primary"${style}>${inline(btn.label)}</span>`;
        });
      if (!buttons.length) return "";
      return `<div class="cta-block"><div class="cta-block__buttons">${buttons.join("")}</div></div>`;
    }
    case "testimonials": {
      const items = (Array.isArray(d.items) ? d.items : []).filter(
        (item: any) => typeof item?.quote === "string" && item.quote.trim()
      );
      if (!items.length) return "";
      const cards = items
        .map((item: any) => {
          const attribution = item.attribution
            ? `<figcaption>${inline(item.attribution)}</figcaption>`
            : "";
          return `<figure class="testimonial"><blockquote><p>${inline(item.quote)}</p></blockquote>${attribution}</figure>`;
        })
        .join("");
      return `<div class="testimonials testimonials--${d.layout === "carousel" ? "carousel" : "grid"}">${cards}</div>`;
    }
    case "barList": {
      // Items are { label, percent } (legacy plain strings = a full bar);
      // mirrors the template renderer's normalization.
      const items = (Array.isArray(d.items) ? d.items : [])
        .map((item: any) =>
          typeof item === "string"
            ? { label: item, percent: 100 }
            : { label: typeof item?.label === "string" ? item.label : "", percent: barPercent(item?.percent) })
        .filter((item: { label: string }) => item.label.trim());
      if (!items.length) return "";
      const color = safeColor(typeof d.color === "string" ? d.color : undefined);
      const style = color ? ` style="--bar-color:${esc(color)}"` : "";
      const rows = items
        .map((item: { label: string; percent: number }) =>
          `<li class="bar-list__item"><span class="bar-list__label">${inline(item.label)}</span>` +
          `<span class="bar-list__bar"><span class="bar-list__fill" style="width:${item.percent}%"></span></span></li>`)
        .join("");
      return `<ul class="bar-list bar-list--${d.align === "right" ? "right" : "left"}"${style}>${rows}</ul>`;
    }
    case "map": {
      const where = typeof d.label === "string" && d.label.trim() ? ` — ${inline(d.label)}` : "";
      return Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lon))
        ? `<div class="pgs-site-embed">Map${where}</div>`
        : "";
    }
    case "gallery":
      return `<div class="pgs-site-embed">Photo gallery</div>`;
    case "form":
      return `<div class="pgs-site-embed">Form</div>`;
    case "contactForm":
      return `<div class="pgs-site-embed">Contact form</div>`;
    case "booking":
      return `<div class="pgs-site-embed">Booking widget</div>`;
    case "menu":
      return `<div class="pgs-site-embed">Menu</div>`;
    default:
      return "";
  }
}

/**
 * Alignment modifier classes for one column on the canvas (mirrors the
 * template's sectionColumnClass; the grid cell class is the canvas's own).
 */
export function columnAlignClasses(column: SectionColumn): string {
  const classes: string[] = [];
  if (column.align) classes.push(`page-section__col--align-${column.align}`);
  if (column.valign && column.valign !== "start") {
    classes.push(`page-section__col--valign-${column.valign}`);
  }
  return classes.join(" ");
}

/** Render a column's blocks to preview HTML ("" when the column is empty). */
export function columnPreviewHtml(blocks: Array<Record<string, any>>): string {
  return (Array.isArray(blocks) ? blocks : []).map(previewBlock).filter(Boolean).join("");
}

/** True when a step will render (has body blocks or a marker image). */
function stepHasContent(step: SectionTimeline["steps"][number]): boolean {
  return Boolean(step.imageKey) || step.blocks.length > 0;
}

/** Inline CSS custom properties for the marker/line colours (or ""). */
function timelineColorStyle(timeline: SectionTimeline): string {
  const vars: string[] = [];
  const marker = safeColor(timeline.markerColor);
  const line = safeColor(timeline.lineColor);
  if (marker) vars.push(`--timeline-marker:${marker}`);
  if (line) vars.push(`--timeline-line:${line}`);
  return vars.length ? ` style="${vars.join(";")}"` : "";
}

/**
 * Render a timeline to preview HTML — same markup as the template's
 * renderTimeline (styled by section-canvas.css under .pgs-site): optional
 * content above/below, then the steps <ol>. Markers show the 1-based number,
 * a marker image, or a plain dot (numbers off). Steps with no content are
 * skipped, matching the published numbering. "" when the timeline is empty.
 */
export function timelinePreviewHtml(timeline: SectionTimeline): string {
  const steps = timeline.steps.filter(stepHasContent);
  const above = columnPreviewHtml(timeline.above ?? []);
  const below = columnPreviewHtml(timeline.below ?? []);
  if (!steps.length && !above && !below) return "";

  const items = steps
    .map((step, index) => {
      const marker = step.imageKey && step.imageUrl
        ? `<span class="timeline__marker timeline__marker--image"><img src="${esc(step.imageUrl)}" alt="" /></span>`
        : timeline.showNumbers
          ? `<span class="timeline__marker">${index + 1}</span>`
          : `<span class="timeline__marker timeline__marker--dot"></span>`;
      const body = columnPreviewHtml(step.blocks);
      return `<li class="timeline__step">${marker}<div class="timeline__body">${body}</div></li>`;
    })
    .join("");

  const list = steps.length
    ? `<ol class="timeline timeline--${timeline.layout}${timeline.showNumbers ? "" : " timeline--dots"}"${timelineColorStyle(timeline)}>${items}</ol>`
    : "";
  const aboveHtml = above ? `<div class="timeline-extra timeline-extra--above">${above}</div>` : "";
  const belowHtml = below ? `<div class="timeline-extra timeline-extra--below">${below}</div>` : "";
  return `${aboveHtml}${list}${belowHtml}`;
}

/** True when no column in the section has any content. */
export function sectionIsEmpty(section: PageSection): boolean {
  if (section.type === "timeline") {
    const timeline = section.timeline;
    if (!timeline) return true;
    return (
      !(timeline.steps ?? []).some(stepHasContent) &&
      !(timeline.above ?? []).length &&
      !(timeline.below ?? []).length
    );
  }
  return section.columns.every((col) => !col.blocks.length);
}

// Mirror of the template's design-token constraints (apps/template/src/lib/
// tokens.ts): key/value shape is validated on write by the API, but sanitise
// again before injecting as inline style variables.
const TOKEN_KEY_RE = /^[a-z][a-z0-9-]*$/;
const TOKEN_UNSAFE_VALUE_RE = /[;{}<>\\\n\r]/;

/**
 * Turn the tenant's design_tokens JSON (from /tenant/config) into CSS custom
 * properties for the canvas wrapper, so the preview picks up the same
 * overrides the published site gets.
 */
export function designTokenStyle(raw: string | null | undefined): CSSProperties {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

  const style: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (!TOKEN_KEY_RE.test(key)) continue;
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > 120 || TOKEN_UNSAFE_VALUE_RE.test(trimmed)) continue;
    style[`--${key}`] = trimmed;
  }
  return style as CSSProperties;
}
